// Enterprise decision-driven dashboard primitives — see /app/memory/DESIGN_SYSTEM.md §20
// Zone order is fixed platform-wide: 対応が必要 → 承認 → アラート → 例外 → 最近の活動 → (任意)指標
import { useState } from 'react';
import { ArrowRight, CaretDown, CaretUp, CheckCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/LanguageContext';

export const DashboardZone = ({ title, hint, action, children, className, testid }) => (
  <section className={cn('space-y-3', className)} data-testid={testid}>
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-bold text-zinc-900">{title}</h2>
        {hint && <p className="text-sm text-zinc-600 mt-0.5">{hint}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

// Single actionable counter. Neutral by default; only a non-zero urgent count
// gets colour, so the eye lands on real work instead of decoration.
export const ActionTile = ({ icon: Icon, label, hint, value, urgent, onClick, testid }) => {
  const n = Number(value) || 0;
  const hot = n > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        'group text-left rounded-lg border bg-white p-4 transition-[border-color,box-shadow]',
        hot && urgent
          ? 'border-rose-300 bg-rose-50/70 shadow-[inset_3px_0_0_0_theme(colors.rose.600)] hover:border-rose-400 hover:shadow-sm'
          : hot ? 'border-zinc-300 shadow-[inset_3px_0_0_0_theme(colors.teal.600)] hover:border-teal-400 hover:shadow-sm'
            : 'border-zinc-200 bg-zinc-100/70'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('flex items-center gap-1.5 text-xs font-semibold truncate', hot && urgent ? 'text-rose-900' : 'text-zinc-600')}>
          {Icon && <Icon size={15} weight="duotone" className={hot && urgent ? 'text-rose-700' : 'text-zinc-500'} />}
          {label}
        </span>
        <ArrowRight size={13} weight="bold" className="shrink-0 text-zinc-400 group-hover:text-teal-700 transition-colors" />
      </div>
      <div className={cn('mt-2 text-3xl font-bold tabular-nums tracking-tight', hot ? (urgent ? 'text-rose-700' : 'text-zinc-900') : 'text-zinc-400')}>
        {value ?? 0}
      </div>
      {hint && <p className="mt-0.5 text-xs text-zinc-500 truncate">{hint}</p>}
    </button>
  );
};

export const ListCard = ({ title, icon: Icon, count, action, children, className, testid }) => (
  <section className={cn('bg-white rounded-lg border border-zinc-300 shadow-sm overflow-hidden', className)} data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
      {Icon && <Icon size={15} weight="duotone" className="text-zinc-500" />}
      <h3 className="text-sm font-bold text-zinc-900 truncate">{title}</h3>
      {count != null && <span className="text-xs font-semibold text-zinc-500 tabular-nums">{count}</span>}
      {action && <div className="ml-auto shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

// "Nothing to do" is a valid, healthy state — never render it like an error.
export const AllClear = ({ message = '対応が必要な項目はありません', testid }) => (
  <div
    data-testid={testid}
    className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-white px-4 py-6 justify-center"
  >
    <CheckCircle size={18} weight="duotone" className="text-emerald-500" />
    <p className="text-sm text-zinc-600">{message}</p>
  </div>
);

// Zone 6 — operational metrics survive only behind an explicit disclosure so
// they never compete with work that needs a decision today.
export const MetricsDisclosure = ({ title = '参考指標', children, testid }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-lg border border-zinc-300 bg-white overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-100 transition-colors"
      >
        {open ? <CaretUp size={13} weight="bold" className="text-zinc-400" /> : <CaretDown size={13} weight="bold" className="text-zinc-400" />}
        <span className="text-sm font-medium text-zinc-700">{title}</span>
        <span className="ml-auto text-xs text-zinc-500">{open ? t('common.close') : t('common.open')}</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-zinc-100" data-testid={testid ? `${testid}-body` : undefined}>{children}</div>}
    </section>
  );
};
